import { useState } from "react";
import { motion, useMotionValue, useMotionTemplate, AnimatePresence } from "framer-motion";
import { Play, X, ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../data/projects";

const ease = [0.76, 0, 0.24, 1];

const ProjectCard = ({ project, onPlay }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const bg = useMotionTemplate`radial-gradient(340px circle at ${mx}px ${my}px, rgba(225,255,0,0.14), transparent 80%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };

  return (
    <motion.div
      data-cursor-hover
      data-testid={`project-card-${project.id}`}
      className={`group relative ${project.span} col-span-12`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      onMouseMove={onMove}
      onClick={() => onPlay(project)}
    >
      <div className="relative overflow-hidden rounded-md border border-white/10 aspect-[16/10] cursor-pointer">
        <img
          src={project.poster}
          alt={project.title}
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <motion.div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: bg }} />

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-full border border-white/30 bg-black/30 backdrop-blur-md transition-all duration-500 group-hover:bg-[#e1ff00] group-hover:scale-110">
            <Play className="h-6 w-6 fill-white text-white transition-colors duration-500 group-hover:fill-black group-hover:text-black ml-1" />
          </div>
        </div>

        {/* Runtime badge */}
        <div className="absolute top-4 right-4 font-outfit text-xs tracking-widest uppercase bg-black/50 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
          {project.runtime}
        </div>

        {/* Index */}
        <div className="absolute top-4 left-4 font-bebas text-2xl text-[#e1ff00] leading-none">
          {project.index}
        </div>
      </div>

      {/* Meta */}
      <div className="flex items-start justify-between mt-5">
        <div>
          <h3 className="font-outfit font-bold text-2xl md:text-3xl uppercase tracking-tight flex items-center gap-2">
            {project.title}
            <ArrowUpRight className="h-5 w-5 text-[#e1ff00] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </h3>
          <p className="font-manrope text-neutral-500 mt-1 text-sm">{project.description}</p>
        </div>
        <div className="text-right shrink-0 pl-4">
          <p className="font-outfit text-xs uppercase tracking-[0.2em] text-[#e1ff00]">
            {project.category}
          </p>
          <p className="font-manrope text-neutral-600 text-sm mt-1">{project.year}</p>
        </div>
      </div>
    </motion.div>
  );
};

export const Showreel = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="work" data-testid="work-section" className="relative py-24 md:py-40">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-end justify-between border-b border-white/10 pb-8 mb-16 flex-wrap gap-4">
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-[#e1ff00] mb-4">
              Selected Work / 2024—2025
            </p>
            <h2 className="font-bebas text-6xl md:text-8xl uppercase leading-[0.85] tracking-tight">
              The Showreel
            </h2>
          </div>
          <p className="font-manrope text-neutral-500 max-w-sm text-base font-light">
            Four worlds built pixel by pixel. Click any frame to roll the tape.
          </p>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-12 gap-x-6 gap-y-16">
          {PROJECTS.map((p) => (
            <ProjectCard key={p.id} project={p} onPlay={setActive} />
          ))}
        </div>
      </div>

      {/* Video modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-lg p-6"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="close-modal"
              className="absolute top-6 right-6 flex items-center gap-2 font-outfit text-sm uppercase tracking-widest text-neutral-300 hover:text-[#e1ff00] transition-colors"
              onClick={() => setActive(null)}
            >
              Close <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ ease }}
              className="w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aspect-video w-full overflow-hidden rounded-md border border-white/10 shadow-[0_0_60px_rgba(225,255,0,0.12)]">
                <iframe
                  data-testid="video-iframe"
                  className="h-full w-full"
                  src={active.embed}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center justify-between mt-4">
                <h3 className="font-bebas text-3xl uppercase tracking-wide">{active.title}</h3>
                <span className="font-outfit text-xs uppercase tracking-[0.2em] text-[#e1ff00]">
                  {active.category} · {active.year}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
